/**
 * Singleton registry for lazily injected page reducers.
 *
 * Page ducks used to be imported statically by `src/containers/reducers.js`, which pulled every
 * page duck (and its transitive dependencies) into the main bundle even though the page components
 * themselves are code-split via @loadable. Each page duck now registers itself here at module
 * evaluation time, so its reducer only reaches the store once its chunk is actually loaded.
 *
 * Consumers:
 *  - `src/reducers.js` builds the reducer manager on top of this registry.
 *  - `src/store.js` subscribes so a late registration triggers `replaceReducer`; SSR stores
 *    unsubscribe after the render (see renderApp).
 */

const reducers = {};
const listeners = new Set();

// Monotonic counter so a reducer manager can cheaply detect "something new was registered"
// without keeping its own subscription. This is what keeps per-request server stores correct:
// they are created before the ducks of the matched route have been imported.
let version = 0;

/**
 * Register (or replace) a reducer under a state key.
 *
 * Idempotent by design: re-registering the exact same reducer function is a no-op, which keeps
 * webpack HMR and React StrictMode double-evaluation from churning the store.
 *
 * @param {string} name state key, e.g. 'ListingPage'
 * @param {Function} reducer the duck's default-exported reducer
 */
export const register = (name, reducer) => {
  if (!name || typeof reducer !== 'function' || reducers[name] === reducer) {
    return;
  }
  reducers[name] = reducer;
  version += 1;
  listeners.forEach((listener) => listener(name, reducer));
};

/** @returns {Object} shallow copy of all reducers registered so far */
export const getReducers = () => ({ ...reducers });

/** @returns {number} increments on every effective registration */
export const getVersion = () => version;

/**
 * Subscribe to registrations.
 *
 * A Set (rather than a single change listener) is required because the SSR process creates one
 * store per request, and those stores can be alive concurrently.
 *
 * @param {Function} listener called as (name, reducer)
 * @returns {Function} unsubscribe
 */
export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Test-only: drop all registrations and listeners. */
export const reset = () => {
  Object.keys(reducers).forEach((key) => delete reducers[key]);
  listeners.clear();
  version += 1;
};

export default { getReducers, getVersion, register, reset, subscribe };
